<template>
    <kiosk-team-details :teamId="props.teamId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full justify-center">
            <div class="max-w-(--breakpoint-sm) w-full">
                <page-header>
                    Sub Teams Features

                    <template v-slot:actions>
                        <vx-button
                            v-if="features.length && !loadingTeams"
                            @click="toggleAll()"
                            :loading="togglingAll"
                            data-test="toggle-all"
                            :disabled="Boolean(failedTeams.length) || team.status === 'closed'"
                            color="muted-dark"
                        >
                            Turn {{ onForAll ? 'Off' : 'On' }} for All
                        </vx-button>
                    </template>
                </page-header>
                <div
                    v-if="!loadingTeams && teams.length && features.length"
                    class="flex flex-col gap-4 mb-6 lg:flex-row lg:justify-between lg:items-center"
                >
                    <div class="w-full lg:max-w-80">
                        <vx-selectable
                            v-model="currentFeature"
                            :clearable="false"
                            label="name"
                            :reduce="(option) => option.id"
                            size="lg"
                            appearance="select"
                            :options="features"
                        ></vx-selectable>
                    </div>
                    <div class="text-sm text-slate-500">
                        Feature active on
                        {{ teams.filter((team) => team.team_attributes?.[currentFeature]).length }}
                        of
                        {{ teams.length }}
                    </div>
                </div>

                <page-loading v-if="(loadingTeams && !teams.length) || !features.length"></page-loading>

                <vx-box
                    v-else-if="!loadingTeams && !teams.length"
                    color="info"
                    class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
                >
                    No sub teams yet.
                </vx-box>

                <vx-expandable-card v-else-if="!loadingTeams && teams.length">
                    <template v-slot:content>
                        <progress-bar
                            class="mb-4"
                            v-model:status="progressStatus"
                            :activeBlock="progressActiveBlock"
                            :blocks="progressBlocks"
                            defaultTitle="Updating features…"
                        ></progress-bar>

                        <div v-if="failedTeams.length && !togglingAll">
                            <vx-alert color="danger" :closeable="false"
                                >Some teams have failed to have their flags turned
                                {{ enableAll ? 'on' : 'off' }}.</vx-alert
                            >

                            <div class="bg-white shadow my-4 p-6 rounded-lg flex flex-col gap-4">
                                <div
                                    v-for="[team, reason] in failedTeams"
                                    :key="team.id"
                                    class="pb-4 border-b border-t-0 border-x-0 border-slate-200 border-solid last-of-type:border-b-0 last-of-type:pb-0"
                                >
                                    <div class="flex items-center gap-2">
                                        <vx-badge color="slate" size="xs"> {{ team.id }}</vx-badge>
                                        <span class="font-bold">{{ team.name }}</span>
                                    </div>
                                    <span class="text-rose-700 vx text-sm">{{ reason }}</span>
                                </div>

                                <vx-button size="lg" @click="retryAll()"
                                    >Retry All <font-awesome-icon widthAuto :icon="faRefresh"></font-awesome-icon
                                ></vx-button>
                            </div>
                        </div>

                        <div class="flex flex-col gap-4">
                            <div
                                v-for="subTeam in teams"
                                :key="subTeam.id"
                                class="flex items-center justify-between"
                                :data-test="`team-feature-${subTeam.id}`"
                            >
                                <div class="flex items-center gap-2">
                                    <vx-badge size="xs" color="slate">{{ subTeam.id }}</vx-badge>
                                    <span class="text-base font-extrabold">
                                        {{ subTeam.name }}
                                    </span>
                                </div>
                                <div class="shrink-0">
                                    <vx-toggle
                                        :disabled="team.status === 'closed'"
                                        :modelValue="Boolean(subTeam.team_attributes?.[currentFeature])"
                                        @update:modelValue="
                                            ($event) =>
                                                $event
                                                    ? enableFeature(subTeam.id, currentFeature)
                                                    : disableFeature(subTeam.id, currentFeature)
                                        "
                                        :loading="subTeam.loading"
                                        size="lg"
                                    >
                                        <template v-slot:yes>On</template>
                                        <template v-slot:no>Off</template>
                                    </vx-toggle>
                                </div>
                            </div>
                        </div>
                    </template>
                </vx-expandable-card>
            </div>
        </div>
    </kiosk-team-details>
</template>

<script setup>
import {
    VxBadge,
    VxBox,
    VxButton,
    VxToggle,
    VxExpandableCard,
    VxSelectable,
    VxAlert,
} from '@voxie/frontend-components';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { adminTeamsClient, adminFeaturesClient } from '~/services';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskTeamDetails from '../details/KioskTeamDetails.vue';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import ProgressBar from '~/components/general/progress/ProgressBar.vue';
import { faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useTeamDetails } from '~/components/kiosk/kiosk-teams/composables/useTeamDetails';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

const { team } = useTeamDetails();

const toasted = useToasted();

const catchRequestError = (e) => {
    if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
        toasted.global.platform_error();
    } else {
        toasted.error(e.response?.data?.message || 'Something went wrong.');
    }
};

const features = ref([
    {
        id: 'notifications',
    },
]);

const getFeatures = async () => {
    try {
        const response = await adminFeaturesClient.getFeatures(props.teamId);
        features.value = response.data.data;
    } catch (e) {
        catchRequestError(e);
    }
};

const currentFeature = ref('notifications');

const onForAll = computed(() => {
    // eslint-disable-next-line no-restricted-syntax
    return teams.value.every((team) => team.team_attributes?.[currentFeature.value]);
});

const {
    loading: loadingTeams,
    list: teams,
    fetchParams: paramsTeams,
    fetchRecords: fetchTeams,
} = useCursorPagination();

const loadTeams = async () => {
    await fetchTeams(adminTeamsClient.listSubTeams(props.teamId, paramsTeams.value));
    await nextTick();
    if (paramsTeams.value.cursor) {
        await loadTeams();
    }
};

const featureName = (featureId) => {
    return features.value.find((feature) => feature.id === featureId)?.name || featureId;
};

const toggleFeature = async (teamId, featureId, enable) => {
    const teamIndex = teams.value.findIndex((team) => team.id == teamId);
    const team = teams.value[teamIndex];

    // eslint-disable-next-line no-restricted-syntax
    team.team_attributes = team.team_attributes || {};

    if (team.loading) {
        return;
    }

    // eslint-disable-next-line no-restricted-syntax
    if (enable == team.team_attributes[featureId]) {
        return;
    }

    team.loading = true;
    try {
        await adminFeaturesClient[enable ? 'enableFeature' : 'disableFeature'](team.id, featureId);

        // eslint-disable-next-line no-restricted-syntax
        team.team_attributes[featureId] = enable;
    } finally {
        team.loading = false;
    }
};

const enableFeature = async (teamId, featureId) => {
    try {
        await toggleFeature(teamId, featureId, true);
        toasted.success(`${featureName(featureId)} has been enabled.`);
    } catch (e) {
        catchRequestError(e);
    }
};

const disableFeature = async (teamId, featureId) => {
    try {
        await toggleFeature(teamId, featureId, false);
        toasted.success(`${featureName(featureId)} has been disabled.`);
    } catch (e) {
        catchRequestError(e);
    }
};

const togglingAll = ref(false);
const enableAll = ref(false);
const teamsCount = ref(0);
const processedTeamsCount = ref(0);
const failedTeams = ref([]);
const retry = ref(false);

const progressStatus = ref('not-started');
const progressActiveBlock = computed(() => {
    return Math.ceil(processedTeamsCount.value / 5);
});
const progressBlocks = computed(() => {
    return Math.ceil(teamsCount.value / 5);
});

watch(progressActiveBlock, () => {
    if (progressActiveBlock.value === progressBlocks.value) {
        progressStatus.value = 'completed';
        if (!failedTeams.value.length) {
            retry.value = false;
        }
    }
});

const retryAll = () => {
    retry.value = true;
    toggleAll();
};
const toggleAll = async () => {
    if (togglingAll.value) {
        return;
    }

    if (!retry.value) {
        enableAll.value = !onForAll.value;
    }
    progressStatus.value = 'running';

    // eslint-disable-next-line no-restricted-syntax
    const updateTeams = teams.value.filter((team) => team.team_attributes?.[currentFeature.value] != enableAll.value);
    teamsCount.value = updateTeams.length;

    processedTeamsCount.value = 0;
    failedTeams.value.length = [];
    togglingAll.value = true;

    for (let team of updateTeams) {
        try {
            await toggleFeature(team.id, currentFeature.value, enableAll.value);
        } catch (e) {
            if (!e?.response?.status || e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
                failedTeams.value.push([team, 'Internal error.']);
                continue;
            }

            try {
                await toggleFeature(team.id, currentFeature.value, enableAll.value);
            } catch (e) {
                if (!e?.response?.status || e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
                    failedTeams.value.push([team, 'Internal error.']);
                    continue;
                }

                failedTeams.value.push([team, e.response?.data?.message || 'Something went wrong']);
            }
        } finally {
            processedTeamsCount.value = processedTeamsCount.value + 1;
        }
    }

    togglingAll.value = false;
};

onMounted(() => {
    loadTeams();
    getFeatures();
});
</script>
